import type { UIMessage } from "ai";
import { z } from "zod";

const generatedAssetImageFineTunePreviewSchema = z.object({
  render: z.object({ component: z.literal("generated_asset_image_fine_tune_confirmation") }),
  summary: z.string().trim().min(1).max(600).nullable().optional(),
  instruction: z.string().trim().min(3).max(2_000),
  generatedAssetId: z.uuid(),
  scheduledPostId: z.uuid(),
  blockId: z.string().trim().min(1).max(120),
  slideIndex: z.number().int().positive().nullable().optional(),
  slideId: z.string().trim().min(1).max(120).nullable().optional(),
  batchName: z.string().trim().min(1).max(200).nullable().optional(),
  prompt: z.string().trim().min(1).max(2_000).nullable().optional(),
  signedUrl: z.url().nullable().optional(),
});

export type AssistantGeneratedAssetImageFineTunePreviewReference = {
  id: string;
  summary: string | null;
  instruction: string;
  generatedAssetId: string;
  scheduledPostId: string;
  blockId: string;
  slideIndex: number | null;
  slideId: string | null;
  batchName: string | null;
  prompt: string | null;
  signedUrl: string | null;
};

export type GeneratedAssetFineTuneCompletionCandidate = {
  generationTaskId: string;
};

export function getAssistantGeneratedAssetImageFineTunePreviewReferences(
  message: Pick<UIMessage, "parts">,
): AssistantGeneratedAssetImageFineTunePreviewReference[] {
  const references: AssistantGeneratedAssetImageFineTunePreviewReference[] = [];

  for (const [index, part] of message.parts.entries()) {
    const candidate = part as {
      type?: string;
      state?: string;
      toolCallId?: string;
      output?: unknown;
    };

    if (
      candidate.type !== "tool-preview_generated_asset_image_fine_tune" ||
      candidate.state !== "output-available"
    ) {
      continue;
    }

    const reference = parseAssistantGeneratedAssetImageFineTunePreviewReference(
      candidate.output,
      typeof candidate.toolCallId === "string" ? candidate.toolCallId : `asset-image-edit-${index}`,
    );
    if (reference) {
      references.push(reference);
    }
  }

  return references;
}

export function isAssistantGeneratedAssetImageFineTuneProposalStale(
  messages: readonly Pick<UIMessage, "id" | "role">[],
  proposalMessageId: string,
) {
  const proposalMessageIndex = messages.findIndex((message) => message.id === proposalMessageId);
  if (proposalMessageIndex === -1) {
    return false;
  }

  return messages.slice(proposalMessageIndex + 1).some((message) => message.role === "user");
}

export function findCompletedGeneratedAssetFineTuneResult<
  TAsset extends GeneratedAssetFineTuneCompletionCandidate,
>(assets: readonly TAsset[], generationTaskId: string | null | undefined): TAsset | null {
  if (!generationTaskId) {
    return null;
  }

  return assets.find((asset) => asset.generationTaskId === generationTaskId) ?? null;
}

function parseAssistantGeneratedAssetImageFineTunePreviewReference(
  value: unknown,
  id: string,
): AssistantGeneratedAssetImageFineTunePreviewReference | null {
  const parsed = generatedAssetImageFineTunePreviewSchema.safeParse(value);
  if (!parsed.success) {
    return null;
  }

  return {
    id,
    summary: parsed.data.summary?.trim() || null,
    instruction: parsed.data.instruction,
    generatedAssetId: parsed.data.generatedAssetId,
    scheduledPostId: parsed.data.scheduledPostId,
    blockId: parsed.data.blockId,
    slideIndex: parsed.data.slideIndex ?? null,
    slideId: parsed.data.slideId ?? null,
    batchName: parsed.data.batchName?.trim() || null,
    prompt: parsed.data.prompt?.trim() || null,
    signedUrl: parsed.data.signedUrl ?? null,
  };
}
